import { useState, useMemo } from 'react';
import { SyncedGoogleMobileDevice } from '../../types';
import { Smartphone, Search, AlertTriangle } from 'lucide-react';

interface GoogleMobileDevicesProps {
  devices: SyncedGoogleMobileDevice[];
}

function isOutdatedOS(device: SyncedGoogleMobileDevice): boolean {
  if (device.isEndOfLife) return true;
  const ver = parseFloat(device.osVersion);
  if (isNaN(ver)) return false;
  const typeLower = device.type.toLowerCase();
  if (typeLower === 'android' || device.os.toLowerCase().includes('android')) {
    return ver < 13;
  }
  if (typeLower === 'ios' || device.os.toLowerCase().includes('ios')) {
    return ver < 16;
  }
  return false;
}

function isStale(lastSync: string | null): boolean {
  if (!lastSync) return true;
  const daysSince = (Date.now() - new Date(lastSync).getTime()) / (1000 * 60 * 60 * 24);
  return daysSince > 30;
}

export default function GoogleMobileDevices({ devices }: GoogleMobileDevicesProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [groupByUser, setGroupByUser] = useState(true);

  const stats = useMemo(() => {
    const active = devices.filter(d => !isStale(d.lastSync)).length;
    const stale = devices.length - active;
    const android = devices.filter(d => d.type.toLowerCase() === 'android' || d.os.toLowerCase().includes('android')).length;
    const ios = devices.filter(d => d.type.toLowerCase() === 'ios' || d.os.toLowerCase().includes('ios')).length;
    const other = devices.length - android - ios;
    const eol = devices.filter(d => isOutdatedOS(d)).length;
    return { total: devices.length, active, stale, android, ios, other, eol };
  }, [devices]);

  const filteredDevices = useMemo(() => {
    if (!searchQuery) return devices;
    const q = searchQuery.toLowerCase();
    return devices.filter(d =>
      d.userName.toLowerCase().includes(q) ||
      d.userEmail.toLowerCase().includes(q) ||
      d.model.toLowerCase().includes(q) ||
      d.os.toLowerCase().includes(q)
    );
  }, [devices, searchQuery]);

  const groupedDevices = useMemo(() => {
    if (!groupByUser) return null;
    const groups: Record<string, SyncedGoogleMobileDevice[]> = {};
    for (const d of filteredDevices) {
      const key = d.userEmail;
      if (!groups[key]) groups[key] = [];
      groups[key].push(d);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredDevices, groupByUser]);

  if (devices.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">
          <Smartphone className="h-5 w-5 inline mr-2" />
          Mobile Devices
        </h3>
        <p className="text-muted-foreground text-sm">No mobile device data available. Trigger a sync to collect Google Workspace data.</p>
      </div>
    );
  }

  const renderDeviceRow = (device: SyncedGoogleMobileDevice, idx: number, showUser: boolean) => {
    const outdated = isOutdatedOS(device);
    const stale = isStale(device.lastSync);
    return (
      <tr key={`${device.deviceId}-${idx}`} className="border-b border-border/50 hover:bg-muted/30">
        {showUser && (
          <td className="py-2 px-3 text-muted-foreground">
            <div>{device.userName}</div>
            <div className="text-xs">{device.userEmail}</div>
          </td>
        )}
        <td className="py-2 px-3 text-foreground">{device.model || 'Unknown'}</td>
        <td className="py-2 px-3">
          <div className="flex items-center gap-1.5">
            <span className={outdated ? 'text-red-600 dark:text-red-400 font-medium' : 'text-foreground'}>
              {device.os} {device.osVersion}
            </span>
            {outdated && (
              <span className="inline-flex items-center gap-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded text-xs font-medium">
                <AlertTriangle className="h-3 w-3" />
                EOL
              </span>
            )}
          </div>
        </td>
        <td className="py-2 px-3 text-muted-foreground capitalize">{device.type}</td>
        <td className="py-2 px-3">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
            device.status === 'approved'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
              : device.status === 'blocked'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
          }`}>
            {device.status}
          </span>
        </td>
        <td className="py-2 px-3 text-xs whitespace-nowrap">
          <span className={stale ? 'text-red-500' : 'text-muted-foreground'}>
            {device.lastSync
              ? new Date(device.lastSync).toLocaleDateString()
              : 'Never'}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="card p-6">
      <h3 className="text-lg font-medium text-foreground mb-4">
        <Smartphone className="h-5 w-5 inline mr-2" />
        Mobile Devices
      </h3>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-foreground">{stats.total}</div>
          <div className="text-xs text-muted-foreground">Total Devices</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.active}</div>
          <div className="text-xs text-green-600 dark:text-green-400">Active</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.stale}</div>
          <div className="text-xs text-yellow-600 dark:text-yellow-400">Stale (30d+)</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-foreground">
            {stats.android} / {stats.ios}{stats.other > 0 ? ` / ${stats.other}` : ''}
          </div>
          <div className="text-xs text-muted-foreground">Android / iOS{stats.other > 0 ? ' / Other' : ''}</div>
        </div>
        {stats.eol > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.eol}</div>
            <div className="text-xs text-red-600 dark:text-red-400">End of Life OS</div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search users, models..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>
        <button
          onClick={() => setGroupByUser(!groupByUser)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            groupByUser
              ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          Group by User
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {!groupByUser && <th className="text-left py-2 px-3 font-medium text-muted-foreground">User</th>}
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Model</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">OS</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Type</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status</th>
              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Last Sync</th>
            </tr>
          </thead>
          <tbody>
            {groupByUser && groupedDevices ? (
              groupedDevices.map(([email, userDevices]) => (
                <>
                  <tr key={`header-${email}`} className="bg-muted/40">
                    <td colSpan={5} className="py-2 px-3 font-medium text-foreground">
                      {userDevices[0].userName}
                      <span className="ml-2 text-xs text-muted-foreground">{email}</span>
                      <span className="ml-2 bg-muted px-1.5 py-0.5 rounded text-xs text-muted-foreground">
                        {userDevices.length} device{userDevices.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                  </tr>
                  {userDevices.map((d, i) => renderDeviceRow(d, i, false))}
                </>
              ))
            ) : (
              filteredDevices.map((d, i) => renderDeviceRow(d, i, true))
            )}
          </tbody>
        </table>
        {filteredDevices.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">No devices match your search.</div>
        )}
      </div>
    </div>
  );
}
